import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ClientMovementRequestFormUI from "./ClientMovementRequestFormUI";
import {
  fetchClientMovementForm,
  updateClientMovementForm,
  clearCurrentClientMovementForm,
} from "../../store/slices/clientMovementFormSlice";

const EditClientMovementRequestForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentClientMovementForm, isLoading, error } = useSelector(
    (state) => state.clientMovementForms
  );

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    client_name: "",
    date_of_movement_request: "",
    purpose_of_visit: "",
    requested_date: "",
    requested_time: "",
    estimated_duration: "",
    family_member_name: "",
    relationship: "",
    contact_number: "",
    agreement_assigned: false,
    agreement_charges: false,
    agreement_payment: false,
    signature: "",

    // Finance fields
    amount: "",
    payment_status: "",
    reference_no: "",
    payment_date: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      dispatch(fetchClientMovementForm(id));
    }
    return () => {
      dispatch(clearCurrentClientMovementForm());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (currentClientMovementForm && currentClientMovementForm.id === parseInt(id)) {
      setFormData({
        patient_id: currentClientMovementForm.patient_id || "",
        case_id: currentClientMovementForm.case_id || "",
        client_name: currentClientMovementForm.client_name || "",
        date_of_movement_request: currentClientMovementForm.date_of_movement_request
          ? currentClientMovementForm.date_of_movement_request.split("T")[0]
          : "",
        purpose_of_visit: currentClientMovementForm.purpose_of_visit || "",
        requested_date: currentClientMovementForm.requested_date
          ? currentClientMovementForm.requested_date.split("T")[0]
          : "",
        requested_time: currentClientMovementForm.requested_time || "",
        estimated_duration: currentClientMovementForm.estimated_duration || "",
        family_member_name: currentClientMovementForm.family_member_name || "",
        relationship: currentClientMovementForm.relationship || "",
        contact_number: currentClientMovementForm.contact_number || "",
        agreement_assigned: !!currentClientMovementForm.agreement,
        agreement_charges: !!currentClientMovementForm.agreement,
        agreement_payment: !!currentClientMovementForm.agreement,
        signature: currentClientMovementForm.signature || "",

        // Finance fields
        amount: currentClientMovementForm.amount || "",
        payment_status: currentClientMovementForm.payment_status || "",
        reference_no: currentClientMovementForm.reference_no || "",
        payment_date: currentClientMovementForm.payment_date ? currentClientMovementForm.payment_date.split("T")[0] : "",
      });
    }
  }, [currentClientMovementForm, id]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_name) newErrors.client_name = "Client Name is required";
    if (!formData.date_of_movement_request) newErrors.date_of_movement_request = "Request Date is required";
    if (!formData.purpose_of_visit) newErrors.purpose_of_visit = "Purpose of Visit is required";
    if (!formData.requested_date) newErrors.requested_date = "Requested Date is required";
    if (!formData.requested_time) newErrors.requested_time = "Requested Time is required";
    if (!formData.estimated_duration) newErrors.estimated_duration = "Estimated Duration is required";
    if (!formData.family_member_name) newErrors.family_member_name = "Family Member Name is required";
    if (!formData.relationship) newErrors.relationship = "Relationship is required";
    if (!formData.contact_number) newErrors.contact_number = "Contact Number is required";
    if (!formData.agreement_assigned || !formData.agreement_charges || !formData.agreement_payment) {
       newErrors.agreement = "You must agree to all conditions";
    }
    if (!formData.signature) newErrors.signature = "Signature/Typed name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields in the form.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const submissionData = {
        ...formData,
        agreement: formData.agreement_assigned && formData.agreement_charges && formData.agreement_payment,
      };
      await dispatch(updateClientMovementForm({ id, formData: submissionData })).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Client Movement Request Form updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Stay on edit page
    } catch (err) {
      let errorMessage = "Failed to update movement request form";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Edit Client Movement Request Form</h5>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4">{error}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <ClientMovementRequestFormUI
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                      <i className="ri-save-3-line me-1"></i> Update
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditClientMovementRequestForm;
